import React, { useState, useRef, useEffect } from 'react';
import { addWatermark } from './watermarkUtils';
import ImageUploader from '../../components/ImageUploader/ImageUploader';
import './Watermark.css';

const Watermark = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [watermarkedImage, setWatermarkedImage] = useState(null);
  const [watermarkText, setWatermarkText] = useState('Watermark');
  const [isCustomImage, setIsCustomImage] = useState(false);
  const [watermarkImage, setWatermarkImage] = useState(null);
  const [position, setPosition] = useState('center');
  const [opacity, setOpacity] = useState(50);
  const [size, setSize] = useState(30);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const canvasRef = useRef(null);
  const watermarkFileInputRef = useRef(null);

  const positionOptions = [
    { value: 'topLeft', label: 'Top Left' },
    { value: 'topRight', label: 'Top Right' },
    { value: 'center', label: 'Center' },
    { value: 'bottomLeft', label: 'Bottom Left' },
    { value: 'bottomRight', label: 'Bottom Right' }
  ];

  const handleImageSelected = (img) => {
    setOriginalImage(img);
    setWatermarkedImage(null);
  };

  const handleWatermarkImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setWatermarkImage(img);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleWatermarkTypeChange = (e) => {
    setIsCustomImage(e.target.value === 'image');
    // Reset the watermark image if switching to text
    if (e.target.value === 'text') {
      setWatermarkImage(null);
    }
  };

  // Apply watermark effect whenever any option changes
  useEffect(() => {
    const applyWatermarkEffect = async () => {
      if (!originalImage) return;
      
      // Validate watermark content
      if (!isCustomImage && !watermarkText.trim()) return;
      if (isCustomImage && !watermarkImage) return;
      
      setIsProcessing(true);
      try {
        const watermarkOptions = {
          position,
          opacity: opacity / 100,
          size: size / 100,
          isCustomImage,
          watermarkText,
          watermarkImage
        };
        
        const result = await addWatermark(originalImage, watermarkOptions, canvasRef.current);
        setWatermarkedImage(result);
      } catch (error) {
        console.error('Error applying watermark:', error);
      } finally {
        setIsProcessing(false);
      }
    };
    
    const debounceTimer = setTimeout(() => {
      applyWatermarkEffect();
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [originalImage, watermarkText, isCustomImage, watermarkImage, position, opacity, size]);

  const handleDownload = () => {
    if (!watermarkedImage) return;
    
    const link = document.createElement('a');
    link.href = watermarkedImage;
    link.download = 'watermarked-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="watermark tool-container">
      <div className="container">
        <div className="tool-header">
          <h1>Watermark Tool</h1>
          <p>Add text or image watermarks to your images with real-time preview</p>
        </div>
        
        <div className="tool-content">
          {!originalImage ? (
            <ImageUploader 
              onImageSelected={handleImageSelected}
              selectedImage={originalImage}
            />
          ) : (
            <div className="watermark-workspace">
              <div className="watermark-controls">
                <div className="control-group">
                  <label>Watermark Type</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        name="watermarkType" 
                        value="text" 
                        checked={!isCustomImage} 
                        onChange={handleWatermarkTypeChange} 
                      />
                      Text
                    </label>
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        name="watermarkType" 
                        value="image" 
                        checked={isCustomImage} 
                        onChange={handleWatermarkTypeChange} 
                      />
                      Image
                    </label>
                  </div>
                </div>
                
                {!isCustomImage ? (
                  <div className="control-group">
                    <label htmlFor="watermark-text">Watermark Text</label>
                    <input 
                      type="text" 
                      id="watermark-text" 
                      value={watermarkText} 
                      onChange={(e) => setWatermarkText(e.target.value)} 
                      placeholder="Enter your watermark text"
                      className="watermark-text-input"
                    />
                  </div>
                ) : (
                  <div className="control-group">
                    <label>Watermark Image</label>
                    <input 
                      type="file" 
                      id="watermark-file-input" 
                      accept="image/*" 
                      onChange={handleWatermarkImageChange} 
                      ref={watermarkFileInputRef}
                      className="file-input"
                    />
                    <label htmlFor="watermark-file-input" className="file-label">
                      {watermarkImage ? 'Change Image' : 'Choose Image'}
                    </label>
                  </div>
                )}
                
                <div className="control-group">
                  <label htmlFor="position-select">Position</label>
                  <select 
                    id="position-select" 
                    value={position} 
                    onChange={(e) => setPosition(e.target.value)}
                    className="position-select"
                  >
                    {positionOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="control-group">
                  <label htmlFor="opacity">Opacity</label>
                  <div className="slider-container">
                    <input 
                      type="range" 
                      id="opacity" 
                      min="10" 
                      max="100" 
                      value={opacity} 
                      onChange={(e) => setOpacity(parseInt(e.target.value))} 
                    />
                    <div className="slider-value">{opacity}%</div>
                  </div>
                </div>
                
                <div className="control-group">
                  <label htmlFor="size">Size</label>
                  <div className="slider-container">
                    <input 
                      type="range" 
                      id="size" 
                      min="5" 
                      max="100" 
                      value={size} 
                      onChange={(e) => setSize(parseInt(e.target.value))} 
                    />
                    <div className="slider-value">{size}%</div>
                  </div>
                </div>
                
                {watermarkedImage && (
                  <div className="control-group">
                    <button className="btn btn-primary download-btn" onClick={handleDownload}>
                      Download Image
                    </button>
                  </div>
                )}
                
                <div className="control-group">
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setOriginalImage(null);
                      setWatermarkedImage(null);
                    }}
                  >
                    Choose Different Image
                  </button>
                </div>
              </div>
              
              <div className="image-preview-section">
                <div className="image-preview-container">
                  <h3>Watermarked Preview</h3>
                  {isProcessing ? (
                    <div className="processing-indicator">Processing...</div>
                  ) : watermarkedImage ? (
                    <div className="image-preview">
                      <img src={watermarkedImage} alt="Watermarked" />
                    </div>
                  ) : (
                    <div className="image-preview">
                      <img src={originalImage.src} alt="Original" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
        </div>
      </div>
    </div>
  );
};

export default Watermark; 